/**
 * Core Tabs Component
 *
 * Tab navigation with multiple visual variants, badges/counts, icons,
 * RBAC-gated tabs, controlled/uncontrolled, and lazy rendering.
 * Respects --border-radius and --primary-color design tokens.
 *
 * Variants:
 *   - default:   Primary underline indicator
 *   - pills:     Filled pill buttons
 *   - underline: Minimal dark underline (no color)
 *   - segment:   Segmented control — button group
 *
 * @example
 *   <Tabs items={[{ id: 'overview', label: 'Overview', content: <p>...</p> }]} />
 *   <Tabs items={tabs} variant="pills" value={active} onChange={setActive} />
 *   <Tabs items={tabs} variant="segment" size="sm" />
 */
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';
import { cn } from '@/lib/utils';

export interface TabItem {
  id: string;
  label: string;
  /** Icon before label */
  icon?: React.ReactNode;
  /** Badge count rendered after label */
  count?: number;
  /** Tab content panel */
  content?: React.ReactNode;
  /** Disable this tab */
  disabled?: boolean;
  /** RBAC: only show if user has permission */
  permission?: string;
}

export interface TabsProps {
  items: TabItem[];
  /** Controlled value — the active tab id */
  value?: string;
  /** Change handler */
  onChange?: (tabId: string) => void;
  /** Default active tab id (uncontrolled) */
  defaultValue?: string;
  /** Visual variant */
  variant?: 'default' | 'pills' | 'underline' | 'segment';
  /** Size */
  size?: 'sm' | 'md';
  /** Full width tabs */
  fullWidth?: boolean;
  /** Only mount active tab's content (lazy rendering) */
  lazy?: boolean;
  /** Render content area — set false for header-only tabs */
  renderContent?: boolean;
  className?: string;
  /** Tab bar extra content (e.g. action buttons on the right side) */
  extra?: React.ReactNode;
}

// ── Sub-component: Single tab trigger ────────────────────────────────────────

interface TabTriggerProps {
  item: TabItem;
  isActive: boolean;
  variant: TabsProps['variant'];
  size: TabsProps['size'];
  fullWidth?: boolean;
  onClick: () => void;
}

const TabTrigger: React.FC<TabTriggerProps> = ({ item, isActive, variant, size, fullWidth, onClick }) => {
  const sizeClasses = size === 'sm' ? 'text-xs px-2.5 h-8' : 'text-sm px-3.5 h-9';

  const baseClasses = cn(
    'inline-flex items-center gap-1.5 font-medium transition-all duration-150 whitespace-nowrap select-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-1',
    item.disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
    fullWidth && 'flex-1 justify-center',
  );

  const variantStyles = {
    default: cn(
      sizeClasses,
      'border-b-2 rounded-none -mb-px',
      isActive
        ? 'border-[var(--primary-color)] text-[var(--primary-color)]'
        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600',
    ),
    underline: cn(
      sizeClasses,
      'border-b-2 rounded-none -mb-px',
      isActive
        ? 'border-slate-900 dark:border-slate-100 text-slate-900 dark:text-slate-100'
        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300',
    ),
    pills: cn(
      sizeClasses,
      isActive
        ? 'bg-[var(--primary-color)] text-white shadow-sm'
        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100',
    ),
    segment: cn(
      sizeClasses,
      isActive
        ? 'bg-white dark:bg-slate-700/80 text-[var(--primary-color)] shadow-sm'
        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300',
    ),
  };

  // Pills → true pill shape; segment → inset radius; default/underline → no radius
  const buttonRadius =
    variant === 'pills'
      ? '9999px'
      : variant === 'segment'
        ? 'calc(var(--border-radius, 6px) - 2px)'
        : undefined;

  // Count badge — consistent size & color across all states
  const countBadgeClass = cn(
    'inline-flex items-center justify-center min-w-[20px] h-[18px] text-[10px] font-semibold px-1.5 rounded-full',
    isActive && variant === 'pills'
      ? 'bg-white/25 text-white'
      : isActive
        ? 'bg-[var(--primary-color)]/10 text-[var(--primary-color)]'
        : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
  );

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      disabled={item.disabled}
      className={cn(baseClasses, variantStyles[variant || 'default'])}
      style={buttonRadius ? { '--core-tabs-tab-radius': buttonRadius } as React.CSSProperties : undefined}
    >
      {item.icon && <span className="flex items-center shrink-0">{item.icon}</span>}
      {item.label}
      {item.count !== undefined && (
        <span className={countBadgeClass}>
          {item.count}
        </span>
      )}
    </button>
  );
};

// ── Main Tabs component ──────────────────────────────────────────────────────

export const Tabs: React.FC<TabsProps> = ({
  items,
  value: controlledValue,
  onChange,
  defaultValue,
  variant = 'default',
  size = 'md',
  fullWidth = false,
  lazy = false,
  renderContent = true,
  className = '',
  extra,
}) => {
  const { user } = useAuth();
  const [internalValue, setInternalValue] = useState(defaultValue || items[0]?.id || '');
  const activeTab = controlledValue ?? internalValue;

  // Filter tabs by RBAC
  const visibleItems = items.filter(
    (item) => !item.permission || hasPermission(user, item.permission),
  );

  const handleChange = (tabId: string) => {
    if (controlledValue === undefined) setInternalValue(tabId);
    onChange?.(tabId);
  };

  // Wrapper style for segment variant
  const isSegment = variant === 'segment';
  const showBorder = variant === 'default' || variant === 'underline';

  return (
    <div className={cn('core-tabs', className)}>
      {/* Tab bar */}
      <div
        className={cn(
          'flex items-center',
          showBorder && 'border-b border-slate-200 dark:border-slate-700',
        )}
      >
        <div
          role="tablist"
          className={cn(
            'flex items-center gap-0.5',
            isSegment && 'bg-slate-100 dark:bg-slate-900 p-1',
            fullWidth && 'flex-1',
          )}
          style={isSegment ? { '--core-tabs-list-radius': 'var(--border-radius, 6px)' } as React.CSSProperties : undefined}
        >
          {visibleItems.map((item) => (
            <TabTrigger
              key={item.id}
              item={item}
              isActive={activeTab === item.id}
              variant={variant}
              size={size}
              fullWidth={fullWidth}
              onClick={() => handleChange(item.id)}
            />
          ))}
        </div>
        {extra && (
          <div className="ml-auto pl-3 flex items-center shrink-0">
            {extra}
          </div>
        )}
      </div>

      {/* Content */}
      {renderContent && (
        <div className="pt-4">
          {lazy
            ? visibleItems.find((item) => item.id === activeTab)?.content
            : visibleItems.map((item) => (
                <div key={item.id} style={{ display: item.id === activeTab ? 'block' : 'none' }}>
                  {item.content}
                </div>
              ))}
        </div>
      )}
    </div>
  );
};

export default Tabs;
